import React, { useState, useMemo } from 'react';
import { Search, Filter, Download, Plus, Eye, Edit, Trash2, CheckCircle, XCircle, X } from 'lucide-react';
import { Incident, SEVERITY_OPTIONS, STATUS_OPTIONS } from '../types/incident';
import { initialCaseTypes } from '../data/caseTypes';
import { initialAgencies } from '../data/agencies';
import { getStateName, getRegionName, getDistrictName } from '../data/geography';
import { exportToCSV } from '../utils/csv';
interface IncidentListProps {
  incidents: Incident[];
  onAdd: () => void;
  onEdit: (incident: Incident) => void;
  onDelete: (id: string) => void;
  onView: (incident: Incident) => void;
  isApprovalView?: boolean;
  onApprove?: (id: string, remarks: string) => void;
  onReject?: (id: string, remarks: string) => void;
}
export function IncidentList({
  incidents,
  onAdd,
  onEdit,
  onDelete,
  onView,
  isApprovalView,
  onApprove,
  onReject
}: IncidentListProps) {
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('');
  const [filterSeverity, setFilterSeverity] = useState('');
  const [filterStatus, setFilterStatus] = useState(isApprovalView ? 'Submitted' : '');
  const [showFilters, setShowFilters] = useState(false);

  // Approval Modal State
  const [approvalModal, setApprovalModal] = useState<{
    isOpen: boolean;
    type: 'Approve' | 'Reject';
    incidentId: string;
  } | null>(null);
  const [remarks, setRemarks] = useState('');
  const [remarksError, setRemarksError] = useState('');
  const handleOpenApproval = (id: string, type: 'Approve' | 'Reject') => {
    setApprovalModal({
      isOpen: true,
      type,
      incidentId: id
    });
    setRemarks('');
    setRemarksError('');
  };
  const handleConfirmApproval = () => {
    if (!approvalModal) return;
    if (approvalModal.type === 'Reject' && !remarks.trim()) {
      setRemarksError('Remarks are mandatory when rejecting an incident.');
      return;
    }
    if (approvalModal.type === 'Approve') {
      onApprove?.(approvalModal.incidentId, remarks);
    } else {
      onReject?.(approvalModal.incidentId, remarks);
    }
    setApprovalModal(null);
  };
  const filteredIncidents = useMemo(() => {
    return incidents.filter(inc => {
      const matchesSearch = inc.id.toLowerCase().includes(searchTerm.toLowerCase()) || inc.location.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesType = filterType ? inc.caseTypeId === filterType : true;
      const matchesSeverity = filterSeverity ? inc.severity === filterSeverity : true;
      const matchesStatus = filterStatus ? inc.status === filterStatus : true;
      return matchesSearch && matchesType && matchesSeverity && matchesStatus;
    });
  }, [incidents, searchTerm, filterType, filterSeverity, filterStatus]);
  const handleExport = () => {
    const exportData = filteredIncidents.map(inc => ({
      'Incident ID': inc.id,
      'Type': initialCaseTypes.find(t => t.id === inc.caseTypeId)?.name || inc.caseTypeId,
      'Date': inc.date,
      'State': getStateName(inc.stateId),
      'Region': getRegionName(inc.stateId, inc.regionId),
      'District': getDistrictName(inc.stateId, inc.regionId, inc.districtId),
      'Location': inc.location,
      'Agency': initialAgencies.find(a => a.id === inc.agencyId)?.name || inc.agencyId,
      'Severity': inc.severity,
      'Status': inc.status,
      'Deaths': inc.numDeaths,
      'Injuries': inc.numInjuries
    }));
    exportToCSV(`incidents_${new Date().toISOString().split('T')[0]}.csv`, exportData);
  };
  return <div className="space-y-6" data-id="element-362" data-mp="3w0ec5g9sqktj">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4" data-id="element-363" data-mp="3h5gh2fn9jh8r">
        <div data-id="element-364" data-mp="3m4az3fdep1rt">
          <h1 className="text-2xl font-bold text-gray-900" data-id="element-365" data-mp="3m3rsgut7hzt3">{isApprovalView ? 'Incident Approval' : 'Incident Management'}</h1>
          <p className="text-sm text-gray-500 mt-1" data-id="element-366" data-mp="25p02spng36nh">
            {isApprovalView ? 'Review and approve submitted incidents' : 'Register, manage, and track national security incidents'}
          </p>
        </div>
        <div className="flex items-center gap-3" data-id="element-367" data-mp="2qxtvn89mywmd">
          <button onClick={handleExport} className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 text-gray-700 text-sm font-medium rounded-md hover:bg-gray-50 transition-colors" data-id="element-368" data-mp="2m5tz7psryluj">
            <Download size={16} data-id="element-369" data-mp="2c7mmxk9fkryv" />
            Export Excel
          </button>
          {!isApprovalView && <button onClick={onAdd} className="flex items-center gap-2 px-4 py-2 bg-blue-800 text-white text-sm font-medium rounded-md hover:bg-blue-900 transition-colors" data-id="element-370" data-mp="39vo9bzlplwb3">
              <Plus size={16} data-id="element-371" data-mp="2zwyt0b9gojwy" />
              Report Incident
            </button>}
        </div>
      </div>

      <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden" data-id="element-372" data-mp="2uydqg56qlo5c">
        <div className="p-4 border-b border-gray-200 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 bg-white" data-id="element-373" data-mp="1ohjzjhggoa6l">
          <div className="relative w-full sm:w-64" data-id="element-374" data-mp="1jhxg2qa1hxpc">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" data-id="element-375" data-mp="3v6c4g7ficaaw" />
            <input type="text" placeholder="Search by ID or Location..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="w-full pl-9 pr-4 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent" data-id="element-376" data-mp="3zhrpcbfxhdz" />
          </div>
          <div className="flex items-center gap-2 w-full sm:w-auto" data-id="element-377" data-mp="mq8zwajoz1qs">
            <button onClick={() => setShowFilters(!showFilters)} className={`flex items-center gap-2 px-4 py-2 border text-sm font-medium rounded-md transition-colors w-full sm:w-auto justify-center ${showFilters ? 'bg-blue-50 border-blue-200 text-blue-700' : 'border-gray-300 text-gray-700 hover:bg-gray-50'}`} data-id="element-378" data-mp="msbxbfn6bx5j">
              <Filter size={16} data-id="element-379" data-mp="2yf077vaqzxw5" />
              Filters
            </button>
          </div>
        </div>

        {showFilters && <div className="p-4 border-b border-gray-200 bg-gray-50 grid grid-cols-1 sm:grid-cols-3 gap-4" data-id="element-380" data-mp="2qfdognldoquf">
            <div data-id="element-381" data-mp="2mfbn6fjk1zfo">
              <label className="block text-xs font-medium text-gray-700 mb-1" data-id="element-382" data-mp="2mfv01huclxmu">Case Type</label>
              <select value={filterType} onChange={e => setFilterType(e.target.value)} className="w-full border border-gray-300 rounded-md text-sm py-1.5 px-2 focus:outline-none focus:ring-2 focus:ring-blue-500" data-id="element-383" data-mp="30ax7i9eufv1r">
                <option value="" data-id="element-384" data-mp="35c4fo9kwew8y">All Types</option>
                {initialCaseTypes.map(t => <option key={t.id} value={t.id} data-id="element-385" data-mp="3j2g9qe11n52q">{t.name}</option>)}
              </select>
            </div>
            <div data-id="element-386" data-mp="3e580x41scde5">
              <label className="block text-xs font-medium text-gray-700 mb-1" data-id="element-387" data-mp="3e6rgesfmakev">Severity</label>
              <select value={filterSeverity} onChange={e => setFilterSeverity(e.target.value)} className="w-full border border-gray-300 rounded-md text-sm py-1.5 px-2 focus:outline-none focus:ring-2 focus:ring-blue-500" data-id="element-388" data-mp="3t2766l95zu5g">
                <option value="" data-id="element-389" data-mp="fkbqm8r60v9n">All Severities</option>
                {SEVERITY_OPTIONS.map(s => <option key={s} value={s} data-id="element-390" data-mp="1kam8jboz5pf">{s}</option>)}
              </select>
            </div>
            {!isApprovalView && <div data-id="element-391" data-mp="6m9qdvf9a6nf">
                <label className="block text-xs font-medium text-gray-700 mb-1" data-id="element-392" data-mp="3o0ooa23bor3o">Status</label>
                <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} className="w-full border border-gray-300 rounded-md text-sm py-1.5 px-2 focus:outline-none focus:ring-2 focus:ring-blue-500" data-id="element-393" data-mp="3srddtbvfze06">
                  <option value="" data-id="element-394" data-mp="3jwp7rji1lsab">All Statuses</option>
                  {STATUS_OPTIONS.map(s => <option key={s} value={s} data-id="element-395" data-mp="352knbuxl9ycy">{s}</option>)}
                </select>
              </div>}
          </div>}

        <div className="overflow-x-auto" data-id="element-396" data-mp="3pincbwm835dz">
          <table className="w-full text-left border-collapse" data-id="element-397" data-mp="3pidhnvq8ga1y">
            <thead data-id="element-398" data-mp="3lgs5ydpfq1tc">
              <tr className="bg-gray-50 border-b border-gray-200 text-xs font-semibold text-gray-500 uppercase tracking-wider" data-id="element-399" data-mp="3limvk5bnii3r">
                <th className="px-6 py-4" data-id="element-400" data-mp="3gkblpulcw7lj">Incident ID</th>
                <th className="px-6 py-4" data-id="element-401" data-mp="3bjxegerkx1ok">Type & Date</th>
                <th className="px-6 py-4" data-id="element-402" data-mp="3bli9s6lpwonh">Location</th>
                <th className="px-6 py-4" data-id="element-403" data-mp="36kug1d63ttpk">Reporting Agency</th>
                <th className="px-6 py-4 text-center" data-id="element-404" data-mp="31tkqsdogwse7">Severity</th>
                <th className="px-6 py-4 text-center" data-id="element-405" data-mp="31r82zjw13nt8">Status</th>
                <th className="px-6 py-4 text-center" data-id="element-406" data-mp="2xsirjwujwgc8">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200" data-id="element-407" data-mp="2stxolpnwzik3">
              {filteredIncidents.length === 0 ? <tr data-id="element-408" data-mp="2nt1q5rmc0om2">
                  <td colSpan={7} className="px-6 py-8 text-center text-gray-500 text-sm" data-id="element-409" data-mp="2nve3frh5aass">
                    No incidents found matching your criteria.
                  </td>
                </tr> : filteredIncidents.map(incident => {
              const typeName = initialCaseTypes.find(t => t.id === incident.caseTypeId)?.name || incident.caseTypeId;
              const agencyName = initialAgencies.find(a => a.id === incident.agencyId)?.name || incident.agencyId;
              const stateName = getStateName(incident.stateId);
              const regionName = getRegionName(incident.stateId, incident.regionId);
              const districtName = getDistrictName(incident.stateId, incident.regionId, incident.districtId);
              return <tr key={incident.id} className="hover:bg-gray-50 transition-colors" data-id="element-410" data-mp="24oy7c80h5tn3">
                      <td className="px-6 py-4 whitespace-nowrap" data-id="element-411" data-mp="24qrc1hrj23sg">
                        <span className="text-sm font-medium text-gray-900" data-id="element-412" data-mp="17912ntkrsx63">{incident.id}</span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap" data-id="element-413" data-mp="1c6aq53olmyra">
                        <div className="text-sm font-medium text-gray-900 truncate max-w-[200px]" title={typeName} data-id="element-414" data-mp="3tl9gz8u0ipm6">{typeName}</div>
                        <div className="text-xs text-gray-500" data-id="element-415" data-mp="2hkqzmb3qmht">{incident.date}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap" data-id="element-416" data-mp="bgfw0v2etqjj">
                        <div className="text-sm text-gray-900" data-id="element-417" data-mp="bfmz0lt96fel">{stateName}</div>
                        <div className="text-xs text-gray-500" data-id="element-418" data-mp="3awunmew82zi7">{regionName}, {districtName}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap" data-id="element-419" data-mp="3fuw5wxltqusm">
                        <div className="text-sm text-gray-900 truncate max-w-[150px]" title={agencyName} data-id="element-420" data-mp="3jv6mt5b5n5m9">{agencyName}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-center" data-id="element-421" data-mp="3oossuemtcb93">
                        <SeverityBadge severity={incident.severity} data-id="element-422" data-mp="2s4tjoy4lcj1x" />
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-center" data-id="element-423" data-mp="2w4u89blemamp">
                        <StatusBadge status={incident.status} data-id="element-424" data-mp="2oluqfadlcr5g" />
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-center" data-id="element-425" data-mp="2jp4bwrhfq3m5">
                        <div className="flex items-center justify-center gap-2" data-id="element-426" data-mp="2fly4hgil66e3">
                          <button onClick={() => onView(incident)} className="p-1.5 bg-gray-100 text-gray-600 rounded hover:bg-gray-200 transition-colors" title="View Details" data-id="element-427" data-mp="2and2pcnorenj">
                            <Eye size={16} data-id="element-428" data-mp="25qcvuzj0i7vd" />
                          </button>
                          
                          {isApprovalView && incident.status === 'Submitted' ? <>
                              <button onClick={() => handleOpenApproval(incident.id, 'Approve')} className="p-1.5 bg-emerald-100 text-emerald-600 rounded hover:bg-emerald-200 transition-colors" title="Approve" data-id="element-429" data-mp="1rx56leielogu">
                                <CheckCircle size={16} data-id="element-430" data-mp="1hy5x5tptdoss" />
                              </button>
                              <button onClick={() => handleOpenApproval(incident.id, 'Reject')} className="p-1.5 bg-red-100 text-red-600 rounded hover:bg-red-200 transition-colors" title="Reject" data-id="element-431" data-mp="18ak4nhv3rrx0">
                                <XCircle size={16} data-id="element-432" data-mp="yd5qxq0kdh8e" />
                              </button>
                            </> : !isApprovalView ? <>
                              <button onClick={() => onEdit(incident)} className="p-1.5 bg-blue-100 text-blue-600 rounded hover:bg-blue-200 transition-colors" title="Edit Incident" data-id="element-433" data-mp="1r11jqjn0iozw">
                                <Edit size={16} data-id="element-434" data-mp="1hbrt787thedw" />
                              </button>
                              <button onClick={() => onDelete(incident.id)} className="p-1.5 bg-red-100 text-red-600 rounded hover:bg-red-200 transition-colors" title="Delete" data-id="element-435" data-mp="1h9xdc8bekcgo">
                                <Trash2 size={16} data-id="element-436" data-mp="29z4e7dybxe53" />
                              </button>
                            </> : null}
                        </div>
                      </td>
                    </tr>;
            })}
            </tbody>
          </table>
        </div>
        
        <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between bg-white" data-id="element-437" data-mp="d1q80tjeu9sv">
          <div className="text-sm text-gray-500" data-id="element-438" data-mp="82dejpy1a4hb">
            Showing <span className="font-medium text-gray-900" data-id="element-439" data-mp="84zuodyzpsb5">{filteredIncidents.length > 0 ? 1 : 0}</span> to <span className="font-medium text-gray-900" data-id="element-440" data-mp="3giyn55mr6vjw">{filteredIncidents.length}</span> of <span className="font-medium text-gray-900" data-id="element-441" data-mp="3bkx4glt8oy8y">{filteredIncidents.length}</span> entries
          </div>
        </div>
      </div>

      {/* Approval Modal */}
      {approvalModal && <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-id="element-442" data-mp="3lh0a41navf0n">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md overflow-hidden" data-id="element-443" data-mp="14rur8ovfoqsi">
            <div className="flex items-center justify-between p-4 border-b border-gray-200" data-id="element-444" data-mp="10qiywbl6drgm">
              <h3 className="text-lg font-semibold text-gray-900" data-id="element-445" data-mp="1w2itick6v1yy">
                {approvalModal.type === 'Approve' ? 'Approve Incident' : 'Reject Incident'}
              </h3>
              <button onClick={() => setApprovalModal(null)} className="text-gray-400 hover:text-gray-600" data-id="element-446" data-mp="252p0dfldt3p5">
                <X size={20} data-id="element-447" data-mp="2a21p65vtckud" />
              </button>
            </div>
            <div className="p-4 space-y-4" data-id="element-448" data-mp="29z5r8hjytw5x">
              <p className="text-sm text-gray-600" data-id="element-449" data-mp="2ezjzo0phb84h">
                You are about to {approvalModal.type.toLowerCase()} incident <span className="font-medium text-gray-900" data-id="element-450" data-mp="2j0voi40j3pbm">{approvalModal.incidentId}</span>.
              </p>
              <div data-id="element-451" data-mp="2ntowd0randrx">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-452" data-mp="2nv9flu85raah">
                  Remarks {approvalModal.type === 'Reject' && <span className="text-red-500" data-id="element-453" data-mp="2xrcodixim76e">*</span>}
                </label>
                <textarea value={remarks} onChange={e => {
              setRemarks(e.target.value);
              if (e.target.value.trim()) setRemarksError('');
            }} rows={3} className={`w-full border rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-2 focus:ring-blue-500 sm:text-sm ${remarksError ? 'border-red-500' : 'border-gray-300'}`} placeholder={approvalModal.type === 'Reject' ? 'Please provide a reason for rejection...' : 'Optional remarks...'} data-id="element-454" data-mp="32q77yn3b3zrk" />
                {remarksError && <p className="mt-1 text-sm text-red-600" data-id="element-455" data-mp="14tpd9ef5o54i">{remarksError}</p>}
              </div>
            </div>
            <div className="px-4 py-3 bg-gray-50 border-t border-gray-200 flex justify-end gap-3" data-id="element-456" data-mp="102qw6cmcf307">
              <button onClick={() => setApprovalModal(null)} className="px-4 py-2 border border-gray-300 text-gray-700 text-sm font-medium rounded-md hover:bg-gray-100 transition-colors" data-id="element-457" data-mp="v3e6zl801jug">
                Cancel
              </button>
              <button onClick={handleConfirmApproval} className={`px-4 py-2 text-white text-sm font-medium rounded-md transition-colors ${approvalModal.type === 'Approve' ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-red-600 hover:bg-red-700'}`} data-id="element-458" data-mp="1nrs7c7mk8s96">
                Confirm {approvalModal.type}
              </button>
            </div>
          </div>
        </div>}
    </div>;
}
function SeverityBadge({
  severity
}: {
  severity: string;
}) {
  const styles = {
    Critical: "bg-red-100 text-red-800 border-red-200",
    High: "bg-orange-100 text-orange-800 border-orange-200",
    Medium: "bg-yellow-100 text-yellow-800 border-yellow-200",
    Low: "bg-green-100 text-green-800 border-green-200"
  }[severity] || "bg-gray-100 text-gray-800 border-gray-200";
  return <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${styles}`} data-id="element-459" data-mp="nkn37f81pbpz">
      {severity}
    </span>;
}
function StatusBadge({
  status
}: {
  status: string;
}) {
  const styles = {
    Approved: "bg-emerald-50 text-emerald-700 border-emerald-200",
    Submitted: "bg-amber-50 text-amber-700 border-amber-200",
    Rejected: "bg-red-50 text-red-700 border-red-200"
  }[status] || "bg-gray-50 text-gray-700 border-gray-200";
  return <span className={`inline-flex items-center px-2.5 py-0.5 rounded-md text-xs font-medium border ${styles}`} data-id="element-460" data-mp="2i1eq75dkvav5">
      {status}
    </span>;
}